import { css } from '@emotion/core';
/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';

import { token } from '@atlaskit/tokens';
import { media } from '@atlassian/bitkit-responsive';

export const Table = styled.table<{
  isLoading?: boolean;
  shouldUseTableLayoutAuto?: boolean;
}>(props => ({
  tableLayout: props.shouldUseTableLayoutAuto ? 'auto' : 'fixed',
  width: '100%',
  pointerEvents: props.isLoading ? 'none' : 'auto',
  /* Atlaskit adds a top margin to tables by default and sets it to 0 with :first-child.
     When we have the loading cover, the table is no longer the first child so the margin comes back.
     We don't want that, so remove the margin altogether here.
   */
  marginTop: 0,
}));

export const TableBody = styled.tbody<{ showHeaders: boolean }>(
  props =>
    !props.showHeaders &&
    css({
      borderWidth: 0,
    })
);

export const Container = styled.div({
  position: 'relative',
  width: '100%',
});

export const TableHeader = styled.th<{
  textAlign?: string;
  width?: string;
  desktopOnly?: boolean;
  mobileHidden?: boolean;
}>`
  color: ${token('color.text.subtlest')};
  vertical-align: middle;
  text-align: ${({ textAlign }) => textAlign || 'initial'};
  padding: ${token('space.050')} ${token('space.100')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ width }) => (width ? `width: ${width};` : null)}

  &:first-of-type {
    padding-left: 0;
  }
  ${({ desktopOnly }) =>
    desktopOnly
      ? media.upToLarge(`
    display: none;
  `)
      : null}
  ${({ mobileHidden }) =>
    mobileHidden
      ? media.upToSmall(`
    display: none;
  `)
      : null}
`;

export const TableHeaderMobileOnly = styled(TableHeader)(
  {
    display: 'none',
  },
  media.upToSmall(`
    display: table-cell;
  `)
);

export const TableHeaderMobileHidden = styled(TableHeader)(
  media.upToSmall(`
    display: none;
  `)
);

export const TableCell = styled.td<{
  centered?: boolean;
  widthWithUnit?: string;
}>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  height: 40px;
  box-sizing: border-box;
  text-align: ${({ centered }) => (centered ? 'center' : 'initial')};
  max-width: 0;
  ${({ widthWithUnit }) => (widthWithUnit ? `width: ${widthWithUnit};` : null)}
`;

export const TableCellMobileOnly = styled(TableCell)(
  {
    display: 'none',
  },
  media.upToSmall(`
    display: table-cell;
  `)
);

export const TableCellMobileHidden = styled(TableCell)(
  media.upToSmall(`
    display: none;
  `)
);

export const RightAlignedTableCell = styled(TableCell)({
  textAlign: 'right',
});

export const ColumnDefinitionMobileHidden = styled.col(
  media.upToSmall(`
    display: none;
  `)
);

export const ColumnDefinitionDesktopOnly = styled.col(
  media.upToLarge(`
    display: none;
  `)
);

export const BylineSection = styled.div`
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  ${media.upToSmall(`
    margin-right: ${token('space.100')};
  `)}

  & + & {
    &::before {
      content: '·';
      margin: 0 ${token('space.100')};
      ${media.upToSmall(`
        display: none;
      `)}
    }
  }
`;

export const TabletByline = styled.div(
  {
    display: 'none',
    marginLeft: token('space.100'),
    font: token('font.body'),
    color: token('color.text.subtlest'),
  },
  media.upToLarge(`
    display: block;
  `)
);
